<template>
  <component
    :is="as"
    :id="id"
    :aria-label="ariaLabel"
    :aria-labelledby="ariaLabelledby"
    :class="classes"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import type { SectionAs } from '@/types/component-types';

const sectionVariants = cva('', {
  variants: {
    tone: {
      default: 'bg-paper-page text-ink',
      muted: 'bg-paper-muted text-ink',
      inverse: 'bg-paper-inverse text-ink-inverse',
      accent: 'bg-accent text-ink-inverse',
    },
    padY: {
      sm: 'py-12 md:py-16',
      md: 'py-16 md:py-20',
      lg: 'py-20 md:py-24',
      xl: 'py-24 md:py-32',
    },
  },
  defaultVariants: {
    tone: 'default',
    padY: 'lg',
  },
});

export type SectionTone = NonNullable<VariantProps<typeof sectionVariants>['tone']>;
export type SectionPadY = NonNullable<VariantProps<typeof sectionVariants>['padY']>;
export type { SectionAs } from '@/types/component-types';

interface Props {
  id?: string;
  tone?: SectionTone;
  padY?: SectionPadY;
  as?: SectionAs;
  ariaLabel?: string;
  ariaLabelledby?: string;
}

const props = withDefaults(defineProps<Props>(), {
  tone: 'default',
  padY: 'lg',
  as: 'section',
});

const classes = computed(() => sectionVariants({ tone: props.tone, padY: props.padY }));
</script>
